import { useEffect, useState } from 'react';

import useBaseFilters from '@/hooks/useBaseFilters';
import usePaginationParams from '@/hooks/usePaginationParams';
import { handleError } from '@/services/handleError';

import { leasingApi } from '../api';
import type { TLeasingOrder, TLegalEntityType } from '../types';

type TProps = {
  requestType: 'individual' | 'legal_entity';
};

const useFetchLeasingRequests = ({ requestType }: TProps) => {
  const [requests, setRequests] = useState<TLeasingOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [legalEntityType, setLegalEntityType] = useState<TLegalEntityType | ''>(
    ''
  );

  const { paginationParams, handlePageChange, debouncedSearchValue } =
    usePaginationParams();

  const { selectedUser, handleUserChange } = useBaseFilters({
    defaultUserKey: 'all'
  });

  const getRequestsPerPage = async () => {
    try {
      setIsLoading(true);
      const resolvedLegalEntityType =
        requestType === 'legal_entity' && legalEntityType
          ? legalEntityType
          : undefined;
      const response = await leasingApi.getAllLeasings({
        filterByUser:
          !selectedUser || selectedUser === 'all' ? '' : selectedUser,
        legalEntityType: resolvedLegalEntityType,
        limit: String(paginationParams.limit),
        offset: String(paginationParams.page),
        requestType,
        searchTerm: paginationParams.searchTerm
      });
      setRequests(response.data);
      setTotal(response.total);
      setIsLoading(false);
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      getRequestsPerPage();
    }, 100);

    return () => {
      clearTimeout(timeout);
    };
  }, [
    requestType,
    selectedUser,
    legalEntityType,
    debouncedSearchValue,
    paginationParams.page,
    paginationParams.limit,
    paginationParams.searchTerm
  ]);

  const handleLegalEntityTypeChange = (value: string) => {
    setLegalEntityType(value === 'ip' || value === 'too' ? value : '');
    handlePageChange(1, paginationParams.limit);
  };

  return {
    getRequestsPerPage,
    handleLegalEntityTypeChange,
    handlePageChange,
    handleUserChange,
    isLoading,
    legalEntityType,
    paginationParams,
    requests,
    selectedUser,
    total
  };
};

export default useFetchLeasingRequests;
